import { Avatar, Grid, Typography, Box, Button, Divider, Table, TableHead, TableRow, TableCell, TableBody, Card, CardContent, Stack, IconButton } from '@mui/material';
import { Assignment, Comment, EmailOutlined, FolderOpen, Payment, SupportAgent, Translate, FactCheck } from '@mui/icons-material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { getOneJobsummury } from '../../redux/Thunk/FreelanceScreenThunk/FreelanceThunk';
import Btn from '../../Components/Ui_elements/Btn';
import { jobAccept } from '../../redux/Thunk/authThunk/login';
import dayjs from 'dayjs';
import APP_CONFIG_URL_ENV from '../../env-config';
import GenericTable from '../../Components/Ui_elements/GenericTable';
import { localizeNumber } from '../../Helper/generalhelper';
import PopupModal from '../../Components/Ui_elements/PopupModal';
import JobRejectModal from '../../Components/JobRejectModal';

const JobAccept = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const { single_job_summary } = useSelector((state) => state.root.FreelanceModule);
    const { user } = useSelector((state) => state?.root?.authUser || {});
    const [formData, setFormData] = useState({});
    const [optionsSelected, setOptionsSelected] = useState({ state: false, option: null });
    const [workInstructions, setWorkInstructions] = useState([]);
    const [confirmModal, setConfirmModal] = useState({ open: false, option: null });
    const [isAcceptedByAnotherUser, setIsAcceptedByAnotherUser] = useState(false);
    const [isSubmittingAction, setIsSubmittingAction] = useState(false);
    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState("");

    const safeJsonParse = (value, fallback) => {
        if (value === null || value === undefined) return fallback;
        if (typeof value !== "string") return value;
        const trimmed = value.trim();
        if (!trimmed) return fallback;
        try {
            return JSON.parse(trimmed);
        } catch (e) {
            return fallback;
        }
    };

    const priceRowsRaw = safeJsonParse(single_job_summary?.price, []);
    const priceRows = Array.isArray(priceRowsRaw) ? priceRowsRaw : [];

    const isLoggedIn = !!localStorage.getItem("token");
    const loggedInUserId = Number(user?.iUserId || 0);
    const urlUserId = Number(formData?.userId || 0);
    const isMismatchedLoggedInUser = isLoggedIn && loggedInUserId > 0 && urlUserId > 0 && loggedInUserId !== urlUserId;

    // STEP 1: Extract & decode token
    useEffect(() => {
        const tokenParam = location.pathname.split("/").pop();
        if (!tokenParam) return;

        try {
            const decoded = atob(tokenParam);
            const obj = Object.fromEntries(new URLSearchParams(decoded));

            if (obj?.jobId && obj?.userId) {
                setFormData({
                    jobId: obj.jobId,
                    userId: obj.userId,
                });
                dispatch(getOneJobsummury(tokenParam));
            }
        } catch (err) {
            console.error("Invalid token:", err);
        }
    }, [location]);


    useEffect(() => {
        if (!single_job_summary || !formData.userId) return;
        const { accept, rejection } = single_job_summary;
        const currentUserId = Number(formData.userId);
        const acceptId = Number(accept);
        const rejectId = Number(rejection);
        // If another user accepted, show message-only screen
        if (acceptId && acceptId !== currentUserId) {
            setIsAcceptedByAnotherUser(true);
            return;
        }
        setIsAcceptedByAnotherUser(false);
        // If current user accepted
        if (acceptId === currentUserId) {
            setOptionsSelected({ state: true, option: 1 });
            return;
        }
        // If current user rejected
        if (rejectId === currentUserId) {
            setOptionsSelected({ state: true, option: 0 });
            return;
        }
        // If rejected by another user → still allow actions
        setOptionsSelected({ state: false, option: null });
    }, [single_job_summary, formData.userId]);


    useEffect(() => {
        const raw = single_job_summary?.work_instruction;
        try {
            const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
            if (Array.isArray(parsed)) {
                setWorkInstructions(parsed.map((item, index) => ({
                    work_id: item?.work_id ?? index,
                    work_name: item?.work_name || '',
                    work_checked: !!item?.work_checked,
                })));
            } else {
                setWorkInstructions([]);
            }
        } catch (err) {
            console.error("Failed to parse work_instruction on update:", err);
            setWorkInstructions([]);
        }
    }, [single_job_summary?.work_instruction]);

    // STEP 2: Accept/Reject API
    const handleActionClick = (option) => {
        if (option === 0) {
            setRejectModalOpen(true);
            return;
        }
        setConfirmModal({ open: true, option });
    };

    const submitAction = async ({ option, note } = {}) => {
        const selectedButton = option;
        const payload = {
            resourceId: formData.userId,
            jobId: formData.jobId,
            jobAccept: selectedButton,
            ...(selectedButton === 0 ? { note: (note || "").trim() } : {}),
        };

        setIsSubmittingAction(true);
        try {
            const resultAction = await dispatch(jobAccept(payload));
            const isSuccess = resultAction?.meta?.requestStatus === 'fulfilled'
                && (resultAction?.payload?.status === 200 || !resultAction?.payload?.status);
            if (isSuccess) {
                setOptionsSelected({ state: true, option: selectedButton });
                const tokenParam = location.pathname.split("/").pop();
                if (tokenParam) {
                    dispatch(getOneJobsummury(tokenParam));
                }
                setConfirmModal({ open: false, option: null });
                setRejectModalOpen(false);
                setRejectReason("");
            }
        } finally {
            setIsSubmittingAction(false);
        }
    };

    const handleConfirmAccept = async () => {
        return submitAction({ option: confirmModal.option });
    };

    const handleConfirmReject = async () => {
        const trimmed = (rejectReason || "").trim();
        if (!trimmed) return;
        return submitAction({ option: 0, note: trimmed });
    };


    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f4f6f8', py: 5 }}>
            <Box
                maxWidth="md"
                sx={{
                    mx: 'auto',
                    backgroundColor: '#fff',
                    borderRadius: 2,
                    boxShadow: 2,
                    p: 3,
                }}
            >
                {isMismatchedLoggedInUser ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Typography variant="h6" fontWeight={600} color="error.main">
                            This job acceptance link is for a different user.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Please open the link while logged in with the correct account.
                        </Typography>
                    </Box>
                ) : isAcceptedByAnotherUser ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Typography variant="h6" fontWeight={600} color="error.main">
                            This job ({single_job_summary?.po_number}) has already been accepted by another freelancer.
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {/* Header */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Avatar
                                src={ `${APP_CONFIG_URL_ENV.REACT_APP_FILEPATH}/images/logo.png`}
                                alt="logo"
                                variant="square"
                                sx={{ width: 183, height: 'auto' }}
                            />
                            <Typography variant="h6" fontWeight={600}>
                                Job Request
                            </Typography>
                        </Box>

                        {/* Header Info and Job Status */}
                        {single_job_summary ? (
                            <Stack spacing={4} sx={{ px: 1, mt: 3 }}>
                        {/* Section: Job Action */}
                        <Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                                <FactCheck sx={{ color: "primary.main" }} />
                                <Typography variant="subtitle1" fontWeight={700}>
                                    Job Action
                                </Typography>
                            </Box>
                            <Card
                                sx={{
                                    borderRadius: 3,
                                    border: "1px solid #edf0f5",
                                    boxShadow: "0 2px 12px 0 rgba(0,0,0,0.04)"
                                }}
                            >
                                <CardContent>
                                    {optionsSelected.state ? (
                                        <Box
                                            sx={{
                                                textAlign: "center",
                                                py: 2,
                                                borderRadius: 2,
                                                bgcolor: optionsSelected.option === 1 ? "#e8f5e9" : "#ffebee",
                                                border: `1px solid ${optionsSelected.option === 1 ? "#c8e6c9" : "#ffcdd2"}`
                                            }}
                                        >
                                            <Typography
                                                variant="body1"
                                                fontWeight={600}
                                                color={optionsSelected.option === 1 ? "success.main" : "error.main"}
                                            >
                                                {optionsSelected.option === 1 ? "Job Accepted – Thank you!" : "Job Rejected"}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <>
                                            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 1 }}>
                                                <Btn
                                                    variant="contained"
                                                    color="primary"
                                                    size="medium"
                                                    sx={{ px: 4 }}
                                                    disabled={isSubmittingAction}
                                                    onClick={() => handleActionClick(1)}
                                                >
                                                    Accept
                                                </Btn>

                                                <Btn
                                                    variant="outlined"
                                                    color="error"
                                                    size="medium"
                                                    sx={{ px: 4 }}
                                                    disabled={isSubmittingAction}
                                                    onClick={() => handleActionClick(0)}
                                                >
                                                    Reject
                                                </Btn>
                                            </Stack>

                                            <Typography variant="caption" color="text.secondary" textAlign="center" display="block">
                                                Please log into your TMS Dashboard for more details.
                                            </Typography>
                                        </>
                                    )}
                                </CardContent>
                            </Card>
                        </Box>

                        {/* Section 1: Job Details and Project Info */}
                        <Box>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Assignment sx={{ fontSize: 20, color: 'primary.main' }} /> Job Details and Project Info
                            </Typography>
                            <Grid container spacing={3}>
                                {/* Basic Job Details */}
                                <Grid item xs={12} md={6}>
                                    <Card
                                        sx={{
                                            height: "100%",
                                            borderRadius: 3,
                                            boxShadow: "0 2px 12px 0 rgba(0,0,0,0.04)",
                                            border: "1px solid #edf0f5",
                                        }}
                                    >
                                        <CardContent>
                                            <Typography
                                                variant="overline"
                                                color="text.secondary"
                                                fontWeight={700}
                                                gutterBottom
                                                sx={{ display: "flex", alignItems: "center", gap: 1 }}
                                            >
                                                <Assignment sx={{ fontSize: 16 }} /> Job Details
                                            </Typography>
                                            <Stack spacing={1.5} mt={1}>
                                                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                                    <Typography variant="body2" color="text.secondary">Number:</Typography>
                                                    <Typography variant="body2" fontWeight={600}>{single_job_summary?.po_number || "—"}</Typography>
                                                </Box>
                                                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                                    <Typography variant="body2" color="text.secondary">Status:</Typography>
                                                    <Box
                                                        sx={{
                                                            px: 1,
                                                            py: 0.2,
                                                            borderRadius: 1,
                                                            bgcolor: single_job_summary?.item_status === "Ongoing" ? "#e3f2fd" : "#f5f5f5",
                                                            color: single_job_summary?.item_status === "Ongoing" ? "#1976d2" : "inherit",
                                                            fontSize: "0.75rem",
                                                            fontWeight: 700,
                                                        }}
                                                    >
                                                        {single_job_summary?.item_status || "—"}
                                                    </Box>
                                                </Box>
                                                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                                    <Typography variant="body2" color="text.secondary">Due Date:</Typography>
                                                    <Typography variant="body2" fontWeight={600} color="error.main">
                                                        {single_job_summary?.due_date && single_job_summary.due_date !== "0000-00-00 00:00:00"
                                                            ? dayjs(single_job_summary.due_date).format("DD.MM.YYYY HH:mm")
                                                            : "—"}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {/* Project Information */}
                                <Grid item xs={12} md={6}>
                                    <Card
                                        sx={{
                                            height: "100%",
                                            borderRadius: 3,
                                            boxShadow: "0 2px 12px 0 rgba(0,0,0,0.04)",
                                            border: "1px solid #edf0f5",
                                        }}
                                    >
                                        <CardContent>
                                            <Typography
                                                variant="overline"
                                                color="text.secondary"
                                                fontWeight={700}
                                                gutterBottom
                                                sx={{ display: "flex", alignItems: "center", gap: 1 }}
                                            >
                                                <SupportAgent sx={{ fontSize: 16 }} /> Project Info
                                            </Typography>
                                            <Stack spacing={1.5} mt={1}>
                                                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                                    <Typography variant="body2" color="text.secondary">Created:</Typography>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {single_job_summary?.created_date ? dayjs(single_job_summary.created_date).format("DD.MM.YYYY") : "—"}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: 'center' }}>
                                                    <Typography variant="body2" color="text.secondary">Language:</Typography>
                                                    <Typography variant="body2" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Translate sx={{ fontSize: 14, color: 'primary.main' }} />
                                                        {(() => {
                                                            const raw = single_job_summary?.ItemLanguage || "";
                                                            if (raw.includes("{")) {
                                                                try {
                                                                    const parts = raw.split(/}>\s*{/).map((part, index, arr) => {
                                                                        if (!part.trim()) return null;
                                                                        if (index !== 0) part = "{" + part;
                                                                        if (index !== arr.length - 1) part = part + "}";
                                                                        return JSON.parse(part);
                                                                    }).filter(Boolean);
                                                                    return parts.map(p => p.sourceLang).filter(Boolean).join(" > ") || "—";
                                                                } catch (e) {
                                                                    return raw;
                                                                }
                                                            }
                                                            return raw || "—";
                                                        })()}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                    <Typography variant="body2" color="text.secondary">Manager:</Typography>
                                                    <Typography variant="body2" fontWeight={600}>{single_job_summary?.projectManager || "—"}</Typography>
                                                </Box>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Section 2: Comment / Instructions */}
                        <Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                                <Comment sx={{ color: "primary.main" }} />
                                <Typography variant="subtitle1" fontWeight={700}>
                                    Comment / Instructions
                                </Typography>
                            </Box>
                            <Card
                                sx={{
                                    borderRadius: 3,
                                    boxShadow: "0 2px 12px 0 rgba(0,0,0,0.04)",
                                    border: "1px solid #edf0f5",
                                    bgcolor: "#fff",
                                }}
                            >
                                <CardContent>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            whiteSpace: "pre-line",
                                            color: "text.primary",
                                            lineHeight: 1.6,
                                            p: 2,
                                            bgcolor: "#fcfdff",
                                            borderRadius: 2,
                                            border: "1px solid #f0f4ff",
                                            minHeight: "120px"
                                        }}
                                    >
                                        {single_job_summary?.description || <span style={{ color: '#aaa', fontStyle: 'italic' }}>No comments provided for this job.</span>}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Box>

                        {/* Section 3: Payment Details */}
                        <Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                                <Payment sx={{ color: "primary.main" }} />
                                <Typography variant="subtitle1" fontWeight={700}>
                                    Payment Details
                                </Typography>
                            </Box>
                            <Card sx={{ borderRadius: 3, overflow: "hidden", border: "1px solid #edf0f5", boxShadow: "0 2px 12px 0 rgba(0,0,0,0.04)" }}>
                                <GenericTable
                                    columns={[
                                        { name: "quantity", label: "Quantity", options: { sort: true } },
                                        { name: "pricelist", label: "Price Unit", options: { sort: true } },
                                        { name: "itemPrice", label: "Unit Price", options: { sort: true } },
                                        {
                                            name: "itemTotal",
                                            label: `Amount in ${single_job_summary?.freelance_currency ?? "EUR"} ${localizeNumber(single_job_summary?.total_price ?? 0)}`,
                                            options: {
                                                customBodyRender: value => {
                                                    return localizeNumber(Number(value));
                                                },
                                            },
                                        },
                                    ]}
                                    data={
                                        priceRows.map((row, index) => ({
                                            ...row,
                                            rowNumber: index + 1,
                                        }))
                                    }
                                    showAction={false}
                                    displayColumns={4}
                                    displayRows={100}
                                    showSearchBox={false}
                                    showPagination={false}
                                    hideToolbar={true}
                                />
                            </Card>
                        </Box>
                            </Stack>
                        ) : (
                            <Box sx={{ textAlign: 'center', py: 5 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Processing your request... Please wait or log into your TMS Dashboard.
                                </Typography>
                            </Box>
                        )}

                        {/* Footer */}
                        <Divider sx={{ my: 3 }} />
                        <Typography variant="caption" display="block" textAlign="center" color="text.secondary">
                            Translation Management System © 2015
                        </Typography>
                    </>
                )}
            </Box>

            {confirmModal.open && (
                <PopupModal
                    open={confirmModal.open}
                    maxWidth="sm"
                    width="600px"
                    title={confirmModal.option === 1 ? "Accept Job" : "Reject Job"}
                    handleClose={() => setConfirmModal({ open: false, option: null })}
                    actions={
                        <>
                            <Btn onClick={handleConfirmAccept} disabled={isSubmittingAction}>Confirm</Btn>
                            <Btn variant="outlined" onClick={() => setConfirmModal({ open: false, option: null })}>Cancel</Btn>
                        </>
                    }
                >
                    <Typography variant="body1">
                        Are you sure you want to {confirmModal.option === 1 ? "accept" : "reject"} this job?
                    </Typography>
                </PopupModal>
            )}

            <JobRejectModal
                open={rejectModalOpen}
                title="Reject Job"
                confirmText="Confirm"
                cancelText="Cancel"
                reason={rejectReason}
                onChangeReason={(e) => setRejectReason(e.target.value)}
                onConfirm={handleConfirmReject}
                onClose={() => setRejectModalOpen(false)}
                isSubmitting={isSubmittingAction}
            />
        </Box>
    );
};

export default JobAccept;